import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Form, Row, Col, Button, Alert, Spinner, Table, Modal, Badge } from 'react-bootstrap';
import {
  useGetMonitoringSettings,
  usePatchMonitoringSettings,
  useGetMonitoringHosts,
  usePostMonitoringHosts,
  usePutMonitoringHostsHostId,
  useDeleteMonitoringHostsHostId,
  usePostMonitoringCheck,
  usePostMonitoringTestAlert,
  getGetMonitoringHostsQueryKey,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import {
  MonitorSettings,
  MonitoredHost,
  MonitorCheckResult,
  MonitorTestStage,
  PostMonitoringCheck200,
  JsonPatchOperation,
} from '../../api/generated/model';

// TLS certificate monitoring settings.
//
// The certificate checked is the one actually served, read from a handshake —
// certbot can renew while nginx carries on serving the old one, so anything
// reading files on disk reports success right up until a visitor sees the
// expiry.

const empty: MonitorSettings = {
  enabled: false,
  warn_days: 21,
  urgent_days: 7,
  critical_days: 2,
  probe_timeout_ms: 10000,
  re_alert_hours: 24,
  slack_webhook_url: '',
  alert_recipients: '',
  from_address: '',
  from_name: '',
  heartbeat_url: '',
};

// What a run did, said plainly. "Checked 3 hosts" on its own leaves the obvious
// question — did anyone get told — unanswered, and silence is the thing this
// feature exists to eliminate.
const runSummary = (res?: PostMonitoringCheck200): string => {
  const n = res?.results?.length ?? 0;
  const alerted = res?.alerted ?? 0;
  const recovered = res?.recovered ?? 0;

  const parts = [`Checked ${n} host${n === 1 ? '' : 's'}.`];
  if (alerted > 0) parts.push(`${alerted} alert${alerted === 1 ? '' : 's'} sent.`);
  if (recovered > 0) parts.push(`${recovered} recovery notice${recovered === 1 ? '' : 's'} sent.`);
  if (alerted === 0 && recovered === 0) parts.push('Nothing needed alerting.');
  return parts.join(' ');
};

// The band on its own cannot describe a host, because the OK band is the empty
// string: a host nobody has probed yet looks exactly like a healthy one. Only
// last_checked_at separates them, so it is checked before the band is read.
const bandBadge = (h: MonitoredHost): { variant: string; label: string } => {
  if (!h.last_checked_at) return { variant: 'secondary', label: 'Never checked' };

  const days = h.days_remaining;
  switch (h.alerted_band) {
    case 'error': return { variant: 'danger', label: 'Unreachable' };
    case 'expired': return { variant: 'danger', label: 'Expired' };
    case 'critical': return { variant: 'danger', label: `${days ?? 0} days` };
    case 'urgent': return { variant: 'warning', label: `${days ?? 0} days` };
    case 'warn': return { variant: 'warning', label: `${days ?? 0} days` };
    default: return { variant: 'success', label: days != null ? `${days} days` : 'OK' };
  }
};

const formatWhen = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const MonitoringTab: React.FC = () => {
  const { data, isLoading, refetch } = useGetMonitoringSettings();
  const patchMutation = usePatchMonitoringSettings();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<MonitorSettings>(empty);
  const [original, setOriginal] = useState<MonitorSettings>(empty);
  const [saving, setSaving] = useState(false);
  // Every message this card produces, rendered beside the buttons rather than
  // at the top of the card. The card is tall enough that an alert above the
  // first field is off-screen from the Save button, which is how a rejected
  // save — enabling monitoring with no heartbeat URL — looked like no response
  // at all, with the switch still showing on because that is local state.
  const [msg, setMsg] = useState<{ variant: string; text: string } | null>(null);
  // The check and test buttons live below the host table, far enough from the
  // settings form that they need their own message rather than one shared
  // banner the reader has to scroll to find.
  const [actionMsg, setActionMsg] = useState<{ variant: string; text: string } | null>(null);
  const [checkResults, setCheckResults] = useState<MonitorCheckResult[] | null>(null);
  const [testStages, setTestStages] = useState<MonitorTestStage[] | null>(null);
  const [running, setRunning] = useState<string | null>(null);

  // `loaded` tracks whether form has actually been seeded from the server, which
  // is not the same question as isLoading.
  //
  // The gap between them is one paint: isLoading goes false as soon as data
  // arrives, but form is still the `empty` placeholder until this effect runs,
  // so the card renders once with defaults. That is what made a saved
  // "Check certificates on a schedule" flash off and then on.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (data) {
      setForm(data);
      setOriginal(data);
      setLoaded(true);
    }
  }, [data]);

  const hasChanges = JSON.stringify(form) !== JSON.stringify(original);

  const set = (k: keyof MonitorSettings, v: any) => {
    setForm(p => ({ ...p, [k]: v }));
    // "Settings saved." stops being true the moment the form is touched again.
    // An error stays: it is what the edit is trying to fix, and clearing it
    // would take the message away exactly when it is being acted on.
    setMsg(m => (m?.variant === 'success' ? null : m));
  };

  // What the status line says, in priority order. An error outranks everything
  // because it is the only one that needs acting on; the dirty note outranks a
  // success because a success that survives the next keystroke is a lie.
  const status = useMemo(() => {
    if (msg?.variant === 'danger') {
      return { text: msg.text, className: 'text-danger', dismissable: true };
    }
    if (hasChanges) {
      return { text: 'Unsaved changes.', className: 'text-muted', dismissable: false };
    }
    if (msg) {
      return { text: msg.text, className: 'text-success', dismissable: false };
    }
    return null;
  }, [msg, hasChanges]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // 'add' creates-or-replaces, so nothing is silently skipped. This is a
      // small singleton, so sending every field is cheap and avoids a diff
      // that could drop one.
      const patches: JsonPatchOperation[] = (Object.keys(form) as (keyof MonitorSettings)[])
        .map(k => ({ op: 'add' as const, path: `/${k}`, value: form[k] }));
      await patchMutation.mutateAsync({ data: patches });
      await refetch();
      setMsg({ variant: 'success', text: 'Settings saved.' });
    } catch (err: any) {
      // The form keeps what was typed. A rejected save means something needs
      // fixing, and the edits are what there is to fix — throwing them away
      // makes the person redo the work before they can act on the message.
      // Discarding is Cancel's job, and only when it is asked for.
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to save monitoring settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const checkMutation = usePostMonitoringCheck();
  const testMutation = usePostMonitoringTestAlert();

  const runCheck = async () => {
    setRunning('check');
    setActionMsg(null);
    setTestStages(null);
    try {
      // No host_ids: every enabled host, exactly as the timer would.
      const res = await checkMutation.mutateAsync({ data: {} });
      setCheckResults(res?.results ?? []);
      // The run wrote to the host rows, so the table below is now stale.
      await queryClient.invalidateQueries({ queryKey: getGetMonitoringHostsQueryKey() });
      setActionMsg({ variant: 'success', text: runSummary(res) });
    } catch (err: any) {
      setActionMsg({ variant: 'danger', text: err?.response?.data?.message || 'Failed to run the check' });
    } finally {
      setRunning(null);
    }
  };

  const sendTestAlert = async () => {
    setRunning('test');
    setActionMsg(null);
    setCheckResults(null);
    try {
      const res: any = await testMutation.mutateAsync();
      setTestStages(res ?? []);
    } catch (err: any) {
      setActionMsg({ variant: 'danger', text: err?.response?.data?.message || 'Failed to send the test alert' });
    } finally {
      setRunning(null);
    }
  };

  const textField = (label: string, k: keyof MonitorSettings, help?: string, type = 'text') => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control type={type} value={String(form[k] ?? '')} onChange={e => set(k, e.target.value)} />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  const numField = (label: string, k: keyof MonitorSettings, help?: string) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="number"
        value={Number(form[k] ?? 0)}
        onChange={e => set(k, parseInt(e.target.value || '0', 10))}
      />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  // Inside the card, like MonitoredHostsCard does, so the page does not reflow
  // when the settings land.
  if (isLoading || !loaded) {
    return (
      <>
        <Card className="mb-3">
          <Card.Header>
            <span><i className="bi bi-shield-check me-2" />Certificate monitoring</span>
          </Card.Header>
          <Card.Body>
            <div className="text-center py-4 text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading settings…
            </div>
          </Card.Body>
        </Card>
        <MonitoredHostsCard />
      </>
    );
  }

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span><i className="bi bi-shield-check me-2" />Certificate monitoring</span>
        </Card.Header>
        <Card.Body>
          <Form.Check
            type="switch"
            id="monitoring-enabled"
            label="Check certificates on a schedule"
            checked={!!form.enabled}
            onChange={e => set('enabled', e.target.checked)}
            className="mb-1"
          />
          {form.enabled && !String(form.heartbeat_url ?? '').trim() && (
            // Advisory, not a gate. Checks run and alerts send without a
            // heartbeat URL; what is lost is being told when the job itself
            // stops. Worth saying, not worth refusing the save over.
            <Alert variant="warning" className="py-2 mb-2">
              No dead-man&rsquo;s switch configured. Checks will still run and alert — but if this job
              stops running, nothing will say so. Set a heartbeat URL below when you have one.
            </Alert>
          )}
          <Form.Text muted className="d-block mb-3">
            Turning this off fails the dead-man&rsquo;s switch rather than quietly stopping. The switch
            answers &ldquo;is anything being checked&rdquo;, and reporting healthy while checking nothing is the
            failure it exists to catch. To pause deliberately, pause the check at your heartbeat provider too.
          </Form.Text>

          <hr />
          <h6 className="text-muted">Thresholds</h6>
          <Row>
            <Col md={3}>{numField('Warn (days)', 'warn_days', 'First alert. 21 days means two renewal attempts have already failed — Let’s Encrypt issues for 90 days and certbot renews at 30 remaining.')}</Col>
            <Col md={3}>{numField('Urgent (days)', 'urgent_days')}</Col>
            <Col md={3}>{numField('Critical (days)', 'critical_days')}</Col>
            <Col md={3}>{numField('Re-alert (hours)', 're_alert_hours', 'How often an unreachable or already-expired host repeats. The expiry thresholds alert once each on the way past; only these two persist and nag.')}</Col>
          </Row>
          <Row>
            <Col md={3}>{numField('Probe timeout (ms)', 'probe_timeout_ms')}</Col>
          </Row>

          <hr />
          <h6 className="text-muted">Where alerts go</h6>
          <Row>
            <Col md={6}>{textField('Slack webhook URL', 'slack_webhook_url', 'Leave blank to skip Slack.')}</Col>
            <Col md={6}>{textField('Alert recipients', 'alert_recipients', 'Comma-separated email addresses. Leave blank to skip email.')}</Col>
          </Row>
          <Row>
            <Col md={6}>{textField('From address', 'from_address', 'Must be a domain we control, or the relay will reject it.')}</Col>
            <Col md={6}>{textField('From name', 'from_name')}</Col>
          </Row>
          <Form.Text muted className="d-block mb-3">
            Admins also get a dashboard notification for every alert, regardless of these settings.
          </Form.Text>

          <hr />
          <h6 className="text-muted">Dead-man&rsquo;s switch</h6>
          {textField('Heartbeat URL', 'heartbeat_url',
            'Pinged after every successful run; /fail is pinged when the job cannot check anything. The provider alerts when the pings stop. This is the only part hosted outside our own infrastructure, deliberately — if the dashboard is down or a deploy breaks the timer, the result is silence, and silence is what "everything is fine" looks like.')}

          <hr />
          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={() => { setForm(original); setMsg(null); }}
              disabled={!hasChanges || saving}
            >
              Cancel
            </Button>
            {/* Always clickable. A Save that greys itself out leaves no way to
                tell "nothing to do" from "this form is refusing me", and the
                answer to a confusing save is not a disabled button. */}
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
            </Button>
          </div>

          {/* One status line, one place, always below the buttons and always
              occupying its row. Rendering the result above the buttons and the
              dirty note below meant every save shoved the buttons down the
              page; a fixed slot cannot move them. */}
          <div className="mt-2 text-end" style={{ minHeight: '1.5rem' }}>
            {status && (
              <small className={status.className}>
                {status.text}
                {status.dismissable && (
                  <Button
                    variant="link"
                    size="sm"
                    className="py-0 ps-2 pe-0 align-baseline"
                    onClick={() => setMsg(null)}
                  >
                    dismiss
                  </Button>
                )}
              </small>
            )}
          </div>

        </Card.Body>
      </Card>

      <MonitoredHostsCard />

      {/* Run and test, below the hosts they act on. These were above the host
          table, which asked the reader to act on a list they had not seen yet. */}
      <Card className="mt-3">
        <Card.Header>
          <span><i className="bi bi-play-circle me-2" />Run and test</span>
        </Card.Header>
        <Card.Body>
          {actionMsg && (
            <Alert variant={actionMsg.variant} dismissible onClose={() => setActionMsg(null)} className="mb-3">
              {actionMsg.text}
            </Alert>
          )}

          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-secondary" size="sm" onClick={runCheck} disabled={hasChanges || running !== null}>
              {running === 'check' ? <><Spinner animation="border" size="sm" className="me-1" />Checking…</> : <><i className="bi bi-search me-1" />Check all hosts now</>}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={sendTestAlert} disabled={hasChanges || running !== null}>
              {running === 'test' ? <><Spinner animation="border" size="sm" className="me-1" />Sending…</> : <><i className="bi bi-send me-1" />Send test alert</>}
            </Button>
          </div>
          <Form.Text muted className="d-block mt-2">
            {hasChanges
              ? 'Save your settings first — both buttons run against what is stored, not what is on screen above.'
              : 'Checking runs the same code the schedule runs, and sends real alerts if a host has got worse. It never pings the dead-man’s switch, so a run here cannot hide a stopped timer. Both work whether or not the schedule is switched on.'}
          </Form.Text>

          {/* Results go in a dismissible Alert, the shape SMTPTab already uses for
              its own test stages. The panel is what marks this as the output of
              a button press rather than standing configuration — read as a bare
              heading and list, a failed channel looks like live system state. */}
          {testStages && (
            <Alert
              variant={testStages.some(s => !s.ok) ? 'danger' : 'success'}
              dismissible
              onClose={() => setTestStages(null)}
              className="mt-3"
            >
              <div className="fw-semibold">
                <i className="bi bi-flask me-2" />
                Test alert — {testStages.some(s => !s.ok)
                  ? `${testStages.filter(s => !s.ok).length} of ${testStages.length} channels failed`
                  : `all ${testStages.length} channels accepted it`}
              </div>
              <ul className="mb-0 mt-2 small list-unstyled">
                {testStages.map((s, i) => (
                  <li key={i}>
                    <i className={`bi ${s.ok ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'} me-2`} />
                    {s.name}{s.detail ? <span className="text-muted"> — {s.detail}</span> : null}
                  </li>
                ))}
              </ul>
              <hr className="my-2" />
              <div className="small text-muted">
                A synthetic alert for <code>test.invalid</code>, not a real certificate. Accepted means the
                channel took it, not that anyone read it — a path that has never delivered a message is not
                a monitor, so confirm each one actually arrived. The dead-man&rsquo;s switch is only resolved
                in DNS here, never pinged: a ping would reset its timer and hide a stopped job.
              </div>
            </Alert>
          )}

          {checkResults && (
            <Alert variant="secondary" dismissible onClose={() => setCheckResults(null)} className="mt-3">
              <div className="fw-semibold mb-2">
                <i className="bi bi-search me-2" />
                Probe results — what the check just found
              </div>
              <Table size="sm" className="mb-0">
                <thead>
                  <tr><th>Host</th><th>Expires</th><th>Days</th><th>Issuer</th><th>Problem</th></tr>
                </thead>
                <tbody>
                  {checkResults.length === 0 && (
                    <tr><td colSpan={5} className="text-muted">No hosts configured.</td></tr>
                  )}
                  {checkResults.map((r, i) => (
                    <tr key={i}>
                      <td>{r.hostname}:{r.port}</td>
                      <td>{formatWhen(r.not_after)}</td>
                      <td>{r.days_remaining ?? '—'}</td>
                      <td>{r.issuer || '—'}</td>
                      <td className="text-danger">{r.probe_error || r.verify_error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

const MonitoredHostsCard: React.FC = () => {
  const { data: hosts, isLoading, refetch } = useGetMonitoringHosts();
  const createMutation = usePostMonitoringHosts();
  const updateMutation = usePutMonitoringHostsHostId();
  const deleteMutation = useDeleteMonitoringHostsHostId();
  const checkMutation = usePostMonitoringCheck();

  const [editing, setEditing] = useState<MonitoredHost | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  // Remove sits next to Edit, which opens a dialog you can back out of. Without
  // a confirmation of its own the destructive one was the only link on the row
  // with no way to change your mind.
  const [confirmRemove, setConfirmRemove] = useState<MonitoredHost | null>(null);

  const rows = useMemo(() => hosts ?? [], [hosts]);

  // One host, on demand. The same endpoint and therefore the same code the
  // schedule runs, so a row checked by hand and a row checked at 06:00 cannot
  // end up saying different things about the same certificate.
  const checkOne = async (host: MonitoredHost) => {
    setChecking(host.id!);
    setError(null);
    try {
      await checkMutation.mutateAsync({ data: { host_ids: [host.id!] } });
      await refetch();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to check ${host.hostname}`);
    } finally {
      setChecking(null);
    }
  };

  const startCreate = () => {
    setEditing({ hostname: '', port: 443, enabled: true });
    setIsNew(true);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        await createMutation.mutateAsync({ data: editing });
      } else {
        await updateMutation.mutateAsync({ hostId: editing.id!, data: editing });
      }
      await refetch();
      setEditing(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to save host');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (host: MonitoredHost) => {
    setError(null);
    try {
      await deleteMutation.mutateAsync({ hostId: host.id! });
      await refetch();
      setConfirmRemove(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to remove host');
      setConfirmRemove(null);
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span><i className="bi bi-hdd-network me-2" />Monitored hosts</span>
        <Button size="sm" variant="outline-primary" onClick={startCreate}>
          <i className="bi bi-plus-lg me-1" />Add host
        </Button>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        <p className="text-muted small">
          Each host is checked by opening a TLS connection and reading the certificate it serves. Every
          certificate with its own renewal path needs its own entry — including this dashboard, whose
          certificate the public site&rsquo;s form submissions depend on.
        </p>

        {isLoading ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : (
          <Table hover size="sm">
            <thead>
              <tr>
                <th>Host</th><th>Status</th><th>Expires</th><th>Issuer</th><th>Last checked</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-muted">No hosts yet.</td></tr>
              )}
              {rows.map(h => {
                const badge = bandBadge(h);
                return (
                  <tr key={h.id}>
                    <td>
                      {h.hostname}{h.port !== 443 ? `:${h.port}` : ''}
                      {!h.enabled && <Badge bg="secondary" className="ms-2">paused</Badge>}
                    </td>
                    <td>
                      <Badge bg={badge.variant}>{badge.label}</Badge>
                      {h.last_error && <div className="small text-danger">{h.last_error}</div>}
                    </td>
                    <td>{formatWhen(h.not_after)}</td>
                    <td>{h.issuer || '—'}</td>
                    <td>{formatWhen(h.last_checked_at)}</td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => checkOne(h)}
                        disabled={checking !== null}
                      >
                        {checking === h.id ? <Spinner animation="border" size="sm" /> : 'Check now'}
                      </Button>
                      <Button variant="link" size="sm" onClick={() => { setEditing({ ...h }); setIsNew(false); }}>Edit</Button>
                      <Button variant="link" size="sm" className="text-danger" onClick={() => setConfirmRemove(h)}>Remove</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={!!confirmRemove} onHide={() => setConfirmRemove(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Remove {confirmRemove?.hostname}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This host will no longer be checked, and what the monitor has learned about it — when it
            last passed, when its certificate expires, and what it has already alerted on — is cleared.
          </p>
          <p className="mb-0 text-muted small">
            You can add {confirmRemove?.hostname} again later, but it comes back as a new host with no
            history, so the next time its certificate declines you will be told again from scratch.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmRemove(null)}>Keep monitoring it</Button>
          <Button variant="danger" onClick={() => confirmRemove && remove(confirmRemove)}>
            Remove host
          </Button>
        </Modal.Footer>
      </Modal>

      {editing && (
        <Modal show onHide={() => setEditing(null)}>
          <Modal.Header closeButton>
            <Modal.Title>{isNew ? 'Add host' : 'Edit host'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Hostname</Form.Label>
              <Form.Control
                value={editing.hostname}
                onChange={e => setEditing({ ...editing, hostname: e.target.value })}
                placeholder="www.arrayscape.com"
              />
              <Form.Text muted>Bare hostname — no scheme, path or port.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Port</Form.Label>
              <Form.Control
                type="number"
                value={editing.port ?? 443}
                onChange={e => setEditing({ ...editing, port: parseInt(e.target.value || '443', 10) })}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="host-enabled"
              label="Check this host"
              checked={editing.enabled !== false}
              onChange={e => setEditing({ ...editing, enabled: e.target.checked })}
            />
            <Form.Text muted>
              A paused host is not checked and cannot alert. If every host is paused the scheduled run
              fails the dead-man&rsquo;s switch, because nothing is being watched.
            </Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving || !editing.hostname.trim()}>
              {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
};

export default MonitoringTab;
